import type { ReportDtoT } from '@mathflat/domain/@entities/(Content)/Report'

import { mpEventInfo } from '@/constant'
import type { Report, Student } from '@/dto'
import type { DefaultResponse } from '@/types/common'
import type { PaginationResponse } from '@/types/common/api'
import maxios from '@/utils/axios/maxios'

// 보고서 API
const reportApi = {
  all: {
    // 실시간 보고서 목록 조회
    getV2ReportRealTime(params: ReportDtoT.GetReportRealTimeV2Request) {
      return maxios.get<ReportDtoT.GetReportRealTimeV2Response[]>(`/v2/report/real-time`, {
        params,
      })
    },

    // 실시간 보고서 메트릭 조회
    getV2ReportRealTimeMetric(params: ReportDtoT.GetReportRealTimeMetricV2Request) {
      return maxios.get<ReportDtoT.GetReportRealTimeMetricV2Response>(
        `/v2/report/real-time/metric`,
        {
          params,
        },
      )
    },

    // 실시간 보고서 설정 조회
    getV2ReportRealTimeSetting(params: ReportDtoT.GetReportRealTimeSettingV2Request) {
      return maxios.get<ReportDtoT.GetReportRealTimeSettingV2Response>(
        `/v2/report/real-time/setting`,
        {
          params,
        },
      )
    },

    // 실시간 보고서 설정 수정
    putV2ReportRealTimeSetting(body: ReportDtoT.PutReportRealTimeSettingV2Request) {
      const { studentGroupKey, schoolType, grade, lessonClassId, studentId, ...bodyData } = body
      const params = { studentGroupKey, schoolType, grade, lessonClassId, studentId }

      return maxios.put<void>(`/v2/report/real-time/setting`, bodyData, { params })
    },

    // 커스텀 보고서 목록 조회
    getV2ReportCustom(params: ReportDtoT.GetReportCustomV2Request) {
      return maxios.get<PaginationResponse<ReportDtoT.GetReportCustomV2Response[]>>(
        `/v2/report/custom`,
        {
          params,
        },
      )
    },

    // 커스텀 보고서 채점 이력 개수 조회
    getV2ReportCustomScoringHistoryCount(
      params: ReportDtoT.GetReportCustomScoringHistoryCountV2Request,
    ) {
      return maxios.get<ReportDtoT.GetReportCustomScoringHistoryCountV2Response>(
        `/v2/report/custom/scoring-history/count`,
        {
          params,
        },
      )
    },

    // 커스텀 보고서 채점 이력 조회
    getV2ReportCustomScoringHistory(params: ReportDtoT.GetReportCustomScoringHistoryV2Request) {
      return maxios.get<ReportDtoT.GetReportCustomScoringHistoryV2Response>(
        `/v2/report/custom/scoring-history`,
        {
          params,
        },
      )
    },

    // 커스텀 보고서 채점 데이터 조회
    postV2ReportCustomScoring(
      body: ReportDtoT.PostReportCustomScoringV2Request,
      signal?: AbortSignal,
    ) {
      return maxios.post<ReportDtoT.PostReportCustomScoringV2Response>(
        `/v2/report/custom/scoring`,
        body,
        { signal },
      )
    },

    // 커스텀 보고서 수정을 위한 채점 데이터 조회
    getV2ReportCustomReportIdScoring(params: ReportDtoT.GetReportCustomReportIdScoringV2Request) {
      return maxios.get<ReportDtoT.GetReportCustomReportIdScoringV2Response>(
        `/v2/report/custom/${params.reportId}/scoring`,
      )
    },

    // 커스텀 보고서 생성
    postV2ReportCustom(body: ReportDtoT.PostReportCustomV2Request) {
      return maxios.post<ReportDtoT.PostReportCustomV2Response>(`/v2/report/custom`, body)
    },

    // 커스텀 보고서 수정
    putV2ReportCustom(body: ReportDtoT.PutReportCustomV2Request) {
      const { reportId, ...bodyData } = body
      return maxios.put<void>(`/v2/report/custom/${reportId}`, bodyData)
    },

    // 커스텀 보고서 학부모앱 공유
    postV2ReportCustomShareParentApp(body: ReportDtoT.PostReportCustomShareParentAppV2Request) {
      return maxios.post<void>(`/v2/report/custom/share/parent-app`, body)
    },

    // 커스텀 보고서 학부모앱 공유 취소
    postV2ReportCustomUnshareParentApp(body: ReportDtoT.PostReportCustomUnshareParentAppV2Request) {
      return maxios.post<void>(`/v2/report/custom/unshare/parent-app`, body)
    },

    // 커스텀 보고서 문자 공유
    postV2ReportCustomShareParentSms(body: ReportDtoT.PostReportCustomShareParentSmsV2Request) {
      return maxios.post<void>(`/v2/report/custom/share/parent-sms`, body)
    },

    // 실시간 보고서 학부모앱 공유
    postReportRealTimeShareParentApp(body: ReportDtoT.PostReportRealTimeShareParentAppRequest) {
      return maxios.post<void>(`/v2/report/real-time/share/parent-app`, body)
    },

    // 실시간 보고서 문자 공유
    postReportRealTimeShareParentSms(body: ReportDtoT.PostReportRealTimeShareParentSmsRequest) {
      return maxios.post<void>(`/v2/report/real-time/share/parent-sms`, body)
    },

    // 커스텀 보고서 링크 공유
    postV2ReportCustomShareByLink(body: ReportDtoT.PostReportCustomShareByLinkV2Request) {
      return maxios.post<ReportDtoT.PostReportCustomShareByLinkV2Response>(
        `/v2/report/custom/${body.reportId}/share/by-link`,
      )
    },

    // 실시간 보고서 링크 공유
    postReportRealTimeShareByLink(body: ReportDtoT.PostReportRealTimeShareByLinkRequest) {
      return maxios.post<ReportDtoT.PostReportRealTimeShareByLinkResponse>(
        `/v2/report/real-time/share/by-link`,
        body,
      )
    },

    // 실시간 보고서 PDF 다운로드
    getV2ReportRealTimeDownload(params: ReportDtoT.GetReportRealTimeDownloadV2Request) {
      return maxios.get<ReportDtoT.GetReportRealTimeDownloadV2Response>(
        `/v2/report/real-time/download`,
        {
          params,
        },
      )
    },

    getCheckDuplicationReport(params: req.CheckDuplicationReport) {
      return maxios.get<DefaultResponse<res.CheckDuplicationReport>>(`/report/duplicate`, {
        params: { ...params, mp_event_name: mpEventInfo.수업보고서중복체크 },
      })
    },
    getNavigationRange(params: req.RecentReport) {
      return maxios.get<DefaultResponse<res.RecentReport>>(`/report/recently`, {
        params: { ...params, mp_event_name: mpEventInfo.최근생성된수업보고서_년월조회 },
        meta: {
          useAbortController: true,
        },
      })
    },
    patchShowReportToParents(payload: req.ShowReportToParents) {
      const { reportIds, opened } = payload
      const eventName = (() => {
        if (reportIds.length > 1) {
          return opened
            ? mpEventInfo.보고서목록_학부모앱_일괄공개전환
            : mpEventInfo.보고서목록_학부모앱_일괄비공개전환
        } else {
          return opened
            ? mpEventInfo.보고서목록_학부모앱_공개전환
            : mpEventInfo.보고서목록_학부모앱_비공개전환
        }
      })()
      return maxios.patch<DefaultResponse<res.RecentReport>>(`/report/open`, payload, {
        params: { mp_event_name: eventName },
      })
    },
    //  그룹 보고서 생성
    postReport(body: req.NewReport, type: 'group' | 'student') {
      // FIXME: axios interceptors의 emptyStringToNull가 2단계 이상 deep한 객체는 변환을 해주지 못함. 논의 필요
      return maxios.post<DefaultResponse<res.CreateReport>>(
        '/report',
        {
          ...body,
          option: {
            ...body.option,
            comment: body.option.comment === '' ? null : body.option.comment,
          },
        },
        {
          params: {
            mp_event_name:
              type === 'group' ? mpEventInfo.그룹수업보고서생성 : mpEventInfo.수업보고서생성,
          },
        },
      )
    },

    getReportsById(params: req.ReportsById) {
      return maxios.get<DefaultResponse<res.SimpleReport[]>>(`/report/ids`, {
        params,
      })
    },
    getCustomReportsV2ById(params: req.CustomReportsV2ById) {
      return maxios.get<ReportDtoT.GetReportCustomV2Response[]>(`/v2/report/custom/by-id`, {
        params,
      })
    },
  },
  student: {
    getStudentReport({
      studentId,
      reportId,
    }: {
      studentId: Student['id']
      reportId: Report['id']
    }) {
      return maxios.get<DefaultResponse<res.Report>>(`/report/${studentId}/${reportId}`, {
        params: { mp_event_name: mpEventInfo.수업보고서조회 },
      })
    },
    // 사용가능한 월 확인
    getMonthlyAvailableReports(studentId: Student['id'], revision: common.Revision) {
      return maxios.get<DefaultResponse<res.MonthlyReportAvailable[]>>(
        `/report/${studentId}/monthly-available`,
        {
          params: { revision, mp_event_name: mpEventInfo.월별수업보고서생성시생성가능한월조회 },
        },
      )
    },
    // 보고서 수정
    putStudentReport(
      studentId: Student['id'],
      reportId: Report['id'],
      report: req.CreateUpdateReport,
    ) {
      return maxios.put<DefaultResponse<res.UpdateReport>>(
        `/report/${studentId}/${reportId}`,
        report,
        {
          params: { mp_event_name: mpEventInfo.수업보고서수정 },
        },
      )
    },
    // 보고서 벌크 삭제
    deleteReports(params: req.DeleteReports) {
      return maxios.delete(`/report`, {
        params: { ...params, mp_event_name: mpEventInfo.수업보고서일괄삭제 },
      })
    },
  },
  group: {
    getAvailableStudentIds(params: req.ReportAvailableStudentIds) {
      return maxios.get<DefaultResponse<res.Student['id'][]>>(`/report/group/student`, {
        params: { ...params, mp_event_name: mpEventInfo.수업보고서생성가능학생조회 },
      })
    },
  },
  oneClick: {
    // 원클릭 보고서 다운로드
    getDownloadOneClick(params: req.OneClickReportParams) {
      return maxios.get<Blob>('/report/worksheet/download', {
        responseType: 'blob',
        params: {
          ...params,
          mp_event_name: mpEventInfo.원클릭보고서_생성버튼선택,
        },
      })
    },
    // 원클릭 보고서 다운로드
    getDownloadOneClickGroup(params: req.OneClickGroupReportDownloadParams) {
      return maxios.get<common.PresignedFile>(`/report/worksheet/v2/group/download`, {
        params: {
          ...params,
          mp_event_name: mpEventInfo.그룹원클릭보고서_다운로드,
        },
      })
    },
    // 원클릭 그룹 보고서 생성 요청 (비동기)
    postCreateOneClickGroupReport(params: req.OneClickGroupReportParams) {
      return maxios.post('/report/worksheet/group', params, {
        params: { mp_event_name: mpEventInfo.그룹원클릭보고서_생성선택 },
      })
    },
    // 자동시험 원클릭 보고서 다운로드 (개인 PDF)
    getDownloadCas(params: req.CasOneClickReportParams) {
      return maxios.get<Blob>('/report/cas/download', {
        responseType: 'blob',
        params,
      })
    },
    // 자동시험 원클릭 그룹 보고서 생성 요청 (비동기)
    postCreateCasGroupReport(params: req.CasOneClickGroupReportParams) {
      return maxios.post('/report/cas/group', params)
    },
    // 자동시험 원클릭 그룹 보고서 zip 다운로드 (보고서 목록 페이지에서 사용)
    getDownloadCasGroup(params: req.CasOneClickGroupReportDownloadParams) {
      return maxios.get<common.PresignedFile>('/report/cas/v2/group/download', {
        params,
      })
    },
  },
  // 보고서 다운로드
  getDownload(params: req.DownloadReports) {
    return maxios.get<common.PresignedFile>(`/report/v2/download`, {
      params: { ...params, mp_event_name: mpEventInfo.수업보고서다운로드 },
    })
  },
}

export default reportApi
